import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import './custom_dropdown.scss';

export const DROPDOWN_OPEN_EVENT_NAME = 'mindsets-dropdown-open-';
export const DROPDOWN_CLOSE_EVENT_NAME = 'mindsets-dropdown-close-';
export const DROPDOWN_POSITION_EVENT_NAME = 'mindsets-dropdown-position-';

const uniq_id = Date.now() + '-' + Math.floor(Math.random() * 100);

function CustomDropdown(
    {
        id,
        trigger,
        items,
        selected,
        container_class = '',
        trigger_class = '',
        content_class = '',
        item_class = '',
        open_on_mouse_over = false,
        close_on_mouse_leave = false,
        no_wrap = false,
        disabled = false,
        before_trigger,
        after_trigger,
        onOpen,
        onClose,
        match_dropdown_width = false,
        ignore_targets,
        max_dropdown_height,
        use_arrow = true,
        keep_force_open = false
    }
) {
    const triggerRef = useRef();
    const contentRef = useRef();
    const containerRef = useRef();
    const [open, setOpen] = useState(false);

    function onTrigger() {
        if (!disabled && contentRef.current.style.display !== 'block') {
            contentRef.current.style.display = 'block';
            position();
            setOpen(true);
        } else if (contentRef.current.style.display === 'block' && contentRef.current['data-keep-open'] === false) {
            contentRef.current.style.display = 'none';
            setOpen(false);
        }
    }

    function onOpenHandler() {
        if (!disabled && contentRef.current.style.display !== 'block') {
            contentRef.current.style.display = 'block';
            position();
            setOpen(true);
            if (typeof onOpen === 'function') {
                onOpen();
            }
        }
    }

    function onCloseHandler() {
        if (contentRef.current.style.display === 'block' && contentRef.current['data-keep-open'] === false) {
            contentRef.current.style.display = 'none';
            setOpen(false);
        }
        if (typeof onClose === 'function') {
            onClose();
        }
    }

    function onOutsideClick(event) {
        if (contentRef.current && contentRef.current.style && contentRef.current.style.display === 'block') {
            function getAllChildren(htmlElement) {
                if (!htmlElement) {
                    return;
                }
                if (htmlElement.children.length === 0) return [htmlElement];

                let allChildElements = [];

                for (let i = 0; i < htmlElement.children.length; i++) {
                    let children = getAllChildren(htmlElement.children[i]);
                    if (children) allChildElements.push(...children);
                }
                allChildElements.push(htmlElement);

                return allChildElements;
            }

            const targetClasses = [...event.target.classList];
            if (!event.target.matches('#' + 'mindsets-dropdown-trigger-' + uniq_id)) {
                const children = getAllChildren(triggerRef.current);
                if (children) {
                    let classes = [];
                    let i;
                    for (i = 0; i < children.length; i++) {
                        classes = classes.concat([...children[i].classList])
                    }
                    const filteredArray = classes.filter(value => targetClasses.includes(value));
                    if (filteredArray.length <= 0) {
                        const class_array = Array.from(targetClasses);
                        let keep_open = contentRef.current['data-keep-open'];
                        if (Array.isArray(ignore_targets) && ignore_targets.length) {
                            ignore_targets.map(target => {
                                if (class_array.includes(target)) {
                                    keep_open = true;
                                }
                            });
                        }
                        if (!keep_open) {
                            onCloseHandler();
                        }
                    }
                }
            }
            event.stopPropagation();
        }
    }

    function position() {
        const dropdownContent = contentRef.current;
        const dropdownTrigger = triggerRef.current;
        if (dropdownContent && dropdownTrigger) {
            const triggerRect = dropdownTrigger.getBoundingClientRect();
            const dropdownContentRect = dropdownContent.getBoundingClientRect();
            // Constrain width
            if (no_wrap === true) {
                dropdownContent.style.whiteSpace = 'nowrap';
            } else {
                dropdownContent.style.whiteSpace = null;
            }

            const body = document.getElementsByTagName('body')[0];

            if ((triggerRect.x + dropdownContent.offsetWidth) >= body.offsetWidth && dropdownContentRect.left >= 0) {
                dropdownContent.style.right = '0';
                dropdownContent.style.left = match_dropdown_width ? '0' : 'unset';
            } else {
                dropdownContent.style.left = '0';
                dropdownContent.style.right = match_dropdown_width ? '0' : 'unset';
            }

            if (triggerRect.y >= window.innerHeight / 2) {
                dropdownContent.style.bottom = dropdownTrigger.offsetHeight + 'px';
                dropdownContent.style.top = 'unset';
                // Constrain height
                if (max_dropdown_height) {
                    dropdownContent.style.maxHeight = max_dropdown_height + 'px';
                    dropdownContent.style.overflowY = 'auto';
                } else {
                    const max_height = triggerRect.y - 10;
                    dropdownContent.style.maxHeight = max_height + 'px';
                    if (dropdownContent.offsetHeight > max_height - 10) {
                        dropdownContent.style.overflowY = 'scroll';
                    } else {
                        dropdownContent.style.overflowY = 'unset';
                    }
                }
            } else {
                dropdownContent.style.bottom = 'unset';
                dropdownContent.style.top = dropdownTrigger.offsetHeight + 'px';
                // Constrain height
                if (max_dropdown_height) {
                    dropdownContent.style.maxHeight = max_dropdown_height + 'px';
                    dropdownContent.style.overflowY = 'auto';
                } else {
                    const max_height = window.innerHeight - triggerRect.y - dropdownTrigger.offsetHeight - 10;
                    dropdownContent.style.maxHeight = max_height + 'px';
                    if (dropdownContent.offsetHeight > max_height - 10) {
                        dropdownContent.style.overflowY = 'scroll';
                    } else {
                        dropdownContent.style.overflowY = 'unset';
                    }
                }
            }
        }
    }

    function getContainerClassName() {
        return "mindsets-dropdown-container " + container_class;
    }

    function getTriggerClassName() {
        return "mindsets-dropdown-trigger " + trigger_class + (disabled ? ' disabled' : '');
    }

    function getContentClassName() {
        return " mindsets-dropdown-content " + content_class;
    }

    function onMouseEnter() {
        if (open_on_mouse_over) {
            onOpenHandler();
        }
    }

    function onMouseLeave() {
        if (close_on_mouse_leave) {
            onCloseHandler();
        }
    }

    function renderTrigger() {
        return (
            <div
                className={getTriggerClassName()}
                onClick={event => {
                    onTrigger();
                    event.stopPropagation();
                }}
                id={'mindsets-dropdown-trigger-' + uniq_id}
                ref={triggerRef}
            >
                {trigger}
                {renderArrow()}
            </div>
        );
    }

    function renderArrow() {
        if (use_arrow) {
            return (
                <span className="mindsets-dropdown-arrow">
                    <i className={open ? "fas fa-angle-up" : 'fas fa-angle-down'}/>
                </span>
            );
        }

        return null;
    }

    useEffect(() => {
        window.addEventListener('resize', position);
        window.addEventListener('scroll', position);
        window.addEventListener('click', onOutsideClick);
        const container = containerRef.current;
        container.addEventListener('mouseenter', onMouseEnter);
        container.addEventListener('mouseleave', onMouseLeave);
        if (id) {
            window.addEventListener(DROPDOWN_OPEN_EVENT_NAME + id, onOpenHandler);
            window.addEventListener(DROPDOWN_CLOSE_EVENT_NAME + id, onCloseHandler);
            window.addEventListener(DROPDOWN_POSITION_EVENT_NAME + id, position);
        }

        return () => {
            window.removeEventListener('resize', position);
            window.removeEventListener('scroll', position);
            window.removeEventListener('click', onOutsideClick);
            container.removeEventListener('mouseenter', onMouseEnter);
            container.removeEventListener('mouseleave', onMouseLeave);
            if (id) {
                window.removeEventListener(DROPDOWN_OPEN_EVENT_NAME + id, onOpenHandler);
                window.removeEventListener(DROPDOWN_CLOSE_EVENT_NAME + id, onCloseHandler);
                window.removeEventListener(DROPDOWN_POSITION_EVENT_NAME + id, position);
            }
        }
    }, []);

    useEffect(() => {
        contentRef.current['data-keep-open'] = keep_force_open
    }, [keep_force_open]);

    return (
        <div className={getContainerClassName()} ref={containerRef}>
            {before_trigger}
            {renderTrigger()}
            {after_trigger}
            <div
                className={getContentClassName()}
                ref={contentRef}
            >
                {Object.keys(items).map(key => <div
                    key={key}
                    className={item_class + ' mindsets-dropdown-content-item ' + (selected === key ? 'active' : '')}
                >{items[key]}</div>)}
            </div>
        </div>
    );
}

CustomDropdown.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trigger: PropTypes.element.isRequired,
    items: PropTypes.objectOf(PropTypes.element).isRequired,
    selected: PropTypes.string,
    container_class: PropTypes.string,
    trigger_class: PropTypes.string,
    content_class: PropTypes.string,
    item_class: PropTypes.string,
    open_on_mouse_over: PropTypes.bool,
    close_on_mouse_leave: PropTypes.bool,
    no_wrap: PropTypes.bool,
    disabled: PropTypes.bool,
    before_trigger: PropTypes.any,
    after_trigger: PropTypes.any,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    match_dropdown_width: PropTypes.bool,
    ignore_targets: PropTypes.arrayOf(PropTypes.string),
    max_dropdown_height: PropTypes.number,
    use_arrow: PropTypes.bool,
    keep_force_open: PropTypes.bool
}

export default CustomDropdown
